import PropTypes from 'prop-types';
import React from 'react';
import styled, { withTheme } from 'styled-components';
import FocusTrap from 'focus-trap-react';
import { RemoveScroll } from 'react-remove-scroll';
import { hook, withValue } from '@g4/automation-hooks';
import Portal from '@g4/boneyard/lib/Portal';
import Box from '@g4/boneyard/lib/Box';
import Text from '@g4/boneyard/lib/Text';
import tracking from '@g4/g4-analytics';

import closeIconDefault from '../../../images/ic-close-blue.svg';

function stopBubble(evt) {
  evt.stopPropagation();
}

const Overlay = styled(Box)`
  height: 100%;
  width: 100%;
`;

export const Wrapper = styled.div`
  position: relative;
  box-shadow: 0 5px 20px 0 rgba(96, 125, 139, 0.5),
    0 5px 5px 0 rgba(120, 144, 156, 0.2);
  border-radius: 10px;
  background-color: white;
  max-width: ${props => props.theme.layout.constrain}px;
  display: flex;
  flex-direction: column;
  white-space: normal;
  max-height: 100%;

  ${props =>
    props.fullWidth &&
    `
      width: 100%;
    `}

  ${props =>
    props.withDefaultPadding &&
    `
    padding: 30px 10px;
    ${props => props.theme.query.md} {
      padding: 30px;
    }
  `}
`;

const CloseIcon = styled.button`
  ${props => props.filter && `filter:${props.filter};`}
  position: absolute;
  z-index: 1;
  top: 10px;
  right: 10px;
  height: 24px;
  width: 24px;
  ${props => props.theme.query.lg} {
    top: 32px;
    right: 32px;
  }
`;

const ModalHeader = styled(Box)`
  text-align: left;
  ${props => props.theme.query.md} {
    text-align: ${props => props.titleAlign};
  }
`;

const Popup = ({
  title,
  titleAlign,
  automationHook,
  open,
  onRequestClose,
  theme,
  children,
  withDefaultPadding = true,
  closeIconPath,
  fullWidth = false,
  className,
  displayCloseIcon,
  modalHeaderProps,
  titleTextColor,
  filter,
  isTrackingEnabled,
  trackingLabel,
}) => {
  const handleKeyDown = e => {
    if (e.keyCode === 27) {
      onRequestClose();
    }
  };

  const trackCloseClick = () => {
    tracking.trackEvent(
      'site_click_tracking',
      'Close Popup',
      trackingLabel || 'Popup Close Icon'
    );
  };

  if (!open) {
    return null;
  }

  return (
    <Portal>
      <div className={className} onClick={onRequestClose}>
        <Overlay
          position="fixed"
          top={0}
          left={0}
          zIndex={999}
          backgroundColor="rgba(51, 51, 51, 0.8)"
        >
          <FocusTrap
            active={true}
            focusTrapOptions={{
              clickOutsideDeactivates: true,
            }}
          >
            {/* This <div/> exists to allow FocusTrap to apply a ref. Box doesn't work, needs to be a <div> */}
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '15px',
              }}
            >
              <RemoveScroll forwardProps>
                <Wrapper
                  onKeyDown={handleKeyDown}
                  {...hook(automationHook)}
                  role="status"
                  onClick={stopBubble}
                  withDefaultPadding={withDefaultPadding}
                  fullWidth={fullWidth}
                >
                  {title && (
                    <ModalHeader
                      pb={6}
                      w="100%"
                      titleAlign={titleAlign}
                      {...modalHeaderProps}
                    >
                      <Text
                        size={22}
                        weight={500}
                        color={
                          titleTextColor
                            ? titleTextColor
                            : theme.colors.common.peacockBlue
                        }
                        automationHook={withValue(automationHook, 'title')}
                      >
                        {title}
                      </Text>

                      <CloseIcon
                        onClick={() => {
                          onRequestClose();
                          isTrackingEnabled && trackCloseClick();
                        }}
                        {...hook(automationHook, 'close')}
                        filter={filter}
                      >
                        <img alt="closeIcon" src={closeIconPath} />
                      </CloseIcon>
                    </ModalHeader>
                  )}
                  <Box
                    position="relative"
                    overflow="auto"
                    automationHook={withValue(automationHook, 'content')}
                  >
                    <>
                      {!title && displayCloseIcon && (
                        <CloseIcon
                          onClick={onRequestClose}
                          {...hook(automationHook, 'close')}
                          filter={filter}
                        >
                          <img alt="closeIcon" src={closeIconPath} />
                        </CloseIcon>
                      )}
                      {children}
                    </>
                  </Box>
                </Wrapper>
              </RemoveScroll>
            </div>
          </FocusTrap>
        </Overlay>
      </div>
    </Portal>
  );
};

Popup.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  titleAlign: PropTypes.oneOf(['left', 'center']),
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  automationHook: PropTypes.string,
  /** Determines if modal is displaying */
  open: PropTypes.bool,
  onRequestClose: PropTypes.func,
  theme: PropTypes.object.isRequired,
  /** all component children nodes */
  children: PropTypes.node,
  withDefaultPadding: PropTypes.bool,
  closeIconPath: PropTypes.string,
  fullWidth: PropTypes.bool,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  displayCloseIcon: PropTypes.bool,
  modalHeaderProps: PropTypes.object,
  titleTextColor: PropTypes.string,
  filter: PropTypes.string,
  isTrackingEnabled: PropTypes.bool,
  trackingLabel: PropTypes.string,
};

Popup.defaultProps = {
  title: '',
  titleAlign: 'center',
  onRequestClose: Function.prototype,
  open: false,
  withDismissible: false,
  noDismissiblePadding: false,
  closeIconPath: closeIconDefault,
  displayCloseIcon: true,
  modalHeaderProps: {},
};

export default withTheme(Popup);
